import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { trpcVanilla } from "@client/trpc";
import { useEffect } from "react";
import { toast } from "sonner";
import { isTransportError } from "@client/offline/offlineCache";
import { trySetItem } from "@client/utils/storageWrite";
import { AuthTokenSaveError, loadAuthToken, saveAuthToken } from "./auth";
import { parseMobileAuthCallback } from "./mobileOAuth";

// Marks that the cold-start launch URL has already been successfully consumed.
// Lives in sessionStorage so it survives the success `window.location.reload()`
// (and a later sign-out reload) but resets on a true cold start, where the
// WebView — and its sessionStorage — is recreated.
const LAUNCH_URL_CONSUMED_KEY = "mobileAuthLaunchUrlConsumed";

// In-memory guard so `appUrlOpen` and `getLaunchUrl()` can't both handle the
// same launch URL in parallel (duplicate exchanges / double reload). Resets on
// reload (the module re-evaluates), which is the only path that needs a retry.
let handlingDeepLink = false;

// A URL whose code this document PROVED burned — the exchange succeeded but
// the token couldn't be stored, or the server rejected the code outright.
// Re-exchanging it (the `appUrlOpen` twin of a cold-start delivery, which
// never consults the consumed marker) can only produce a second, misleading
// "expired" toast over the accurate one already shown. In-memory on purpose:
// it must never outlive the document and suppress a genuinely fresh link.
let burnedLaunchUrl: string | null = null;

/**
 * Listens for the app being opened via its custom URL scheme and, if the
 * incoming URL carries a short-lived authorization code (the magic-link deep
 * link from the email confirmation page), exchanges it for the session token
 * and reloads so the session is picked up.
 *
 * The interactive Google flow does NOT rely on this — ASWebAuthenticationSession
 * returns its callback URL directly to the plugin. This handler covers the
 * magic-link path, where the link is tapped outside the app (in Mail/Safari)
 * and relaunches it.
 */
export function useMobileAuthDeepLink(): void {
  useEffect(() => {
    // Runs on any native shell (iPad + iPhone). The magic-link deep link
    // returns into the app via the custom URL scheme regardless of device.
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    async function applyDeepLink(
      url: string | undefined,
      { fromColdStart }: { fromColdStart: boolean },
    ): Promise<void> {
      if (!url || handlingDeepLink || url === burnedLaunchUrl) {
        return;
      }

      const { code, returnTo } = parseMobileAuthCallback(url);

      if (!code) {
        return;
      }

      handlingDeepLink = true;

      try {
        // Magic-link codes are unbound (no PKCE verifier) — short-lived and
        // single-use, gated by the emailed link itself.
        const { sessionToken } =
          await trpcVanilla.exchangeMobileAuthCode.mutate({ code });
        const bearerChanged = saveAuthToken(sessionToken);
        // Mark the launch URL consumed, then navigate to pick up the session.
        // After that `getLaunchUrl()` returns this same URL, but the flag
        // makes us skip it (no loop), and it keeps sign-out sticky (the
        // persisted flag blocks a replay). Failed cold-start exchanges seal
        // the URL too (see the catch) — a re-tap retries via `appUrlOpen` or
        // a fresh process, so sealing never burns a still-valid link.
        // Best-effort: with the token saved, sign-in must proceed even if the
        // marker can't be written (the catch's sealed-probe keeps the replay
        // silent).
        trySetItem(window.sessionStorage, LAUNCH_URL_CONSUMED_KEY, "1");

        if (returnTo && returnTo !== "/") {
          window.location.replace(returnTo);
        } else if (bearerChanged) {
          // Identity transition: a reload would inherit the current URL, which
          // on a cold launch the boot redirect may already have rewritten to
          // the PREVIOUS user's saved round. Land the new session on "/".
          window.location.replace("/");
        } else {
          window.location.reload();
        }
      } catch (error) {
        // Expired/replayed codes are expected (e.g. re-opening an old link).
        // Release the guard so a fresh open (user re-taps) can retry, and tell
        // the user instead of failing silently.
        handlingDeepLink = false;
        console.error("Failed to exchange mobile auth code", error);

        if (error instanceof AuthTokenSaveError) {
          // The exchange succeeded but the token couldn't be stored (device
          // storage full). A fresh link fails the same way, so the generic
          // "expired" message would send the user in circles. The code IS
          // burned though: remember that (blocks the twin) and seal the
          // launch URL (blocks reload replays — sessionStorage is separate
          // from the full localStorage, so this write usually lands), or a
          // re-exchange would toast "expired" over this explanation.
          burnedLaunchUrl = url;
          trySetItem(window.sessionStorage, LAUNCH_URL_CONSUMED_KEY, "1");
          toast.error(
            "Couldn't save your sign-in — this device's storage is full. Free up some space, then request a new sign-in link.",
          );
          return;
        }

        if (isTransportError(error)) {
          // The server never adjudicated the code — the link may be perfectly
          // valid. Leave the launch URL unsealed so a reload retries once
          // connectivity returns, and don't claim the link expired.
          toast.error(
            "Couldn't reach the server to finish signing in — check your connection and try the link again.",
          );
          return;
        }

        // The server rejected the code (burned or expired) — a re-exchange
        // of this same URL can only fail again; remember that in memory so
        // the cold-start twin doesn't toast twice.
        burnedLaunchUrl = url;

        if (fromColdStart) {
          // Also seal the launch URL so later reloads don't re-run the
          // exchange and re-toast. Sealing doesn't burn a still-valid link:
          // re-tapping retries via `appUrlOpen` (app alive) or a fresh
          // process (app dead), both unaffected by this marker.
          const sealed = trySetItem(
            window.sessionStorage,
            LAUNCH_URL_CONSUMED_KEY,
            "1",
          );

          if (!sealed && loadAuthToken() != null) {
            // The seal write is also the proof that separates two worlds. If
            // it landed, storage works, so a consumed marker could not have
            // been silently lost — this is a FRESH cold-start link (a signed
            // -in user tapping a magic link that launched the app) and the
            // failure must be surfaced. If even this 1-byte write fails,
            // we're under the storage pressure where lost-marker replays of
            // the already-burned code happen on every reload — stay silent
            // rather than toast "expired" at an already-signed-in user.
            return;
          }
        }

        toast.error(
          "That sign-in link didn't work — it may have expired. Please request a new one.",
        );
      }
    }

    // `appUrlOpen` fires once per real deep-link open (no replay).
    const listenerHandle = App.addListener("appUrlOpen", ({ url }) => {
      void applyDeepLink(url, { fromColdStart: false });
    });

    // Cold start: the app may have been launched by the deep link itself, where
    // `appUrlOpen` does not fire. `getLaunchUrl()` keeps returning that same URL
    // for the life of the app process — across reloads and even after sign-out —
    // so skip it once it's been consumed.
    void App.getLaunchUrl().then((result) => {
      if (window.sessionStorage.getItem(LAUNCH_URL_CONSUMED_KEY)) {
        return;
      }

      void applyDeepLink(result?.url, { fromColdStart: true });
    });

    return () => {
      void listenerHandle.then((handle) => handle.remove());
    };
  }, []);
}
